/**
 * CloudClient — a `DeviceClient` that talks to the `ema-comfort` API instead of
 * to a browser (the seam `DeviceProvider` documents). Today it backs a
 * third-party (Hive) plant, which is **read-only** (ADR-060 §1): it polls
 * `GET /v1/plants/{id}/readings`, maps the result to the domain shapes, and
 * refuses every command with `sorgente_non_comandabile`. The technical area
 * does not exist for such a plant, so its methods reject rather than pretend.
 *
 * Freshness is never faked. A vendor-down poll comes back from the API as a
 * degraded reading (200, `raggiungibile: false`), and a spent credential as
 * 409 `reconnect_required`; both leave the source marked unreachable rather
 * than showing a stale number.
 */

import {
  ErroreRichiesta,
  type Ascoltatore,
  type AreaTecnicaClient,
  type DeviceClient,
  type Istantanea,
  type PuntoStorico,
  type RichiestaStorico,
} from '../DeviceClient';
import type {
  Allarme,
  Comando,
  EventoStato,
  Impianto,
  ProgrammaOrario,
  RispostaComando,
  StatoImpianto,
} from '@emacomfort/domain';
import { costruisciImpianto, costruisciIstantanea, programmaZona } from './mappa';
import type { ImpiantoRegistro, LetturaImpiantoWire } from './tipi';

export interface OpzioniCloud {
  impiantoId: string;
  /** e.g. https://app.emacomfort.com/comfort — the API base, path included. */
  baseUrl: string;
  /** Supplies the caller's bearer token; called per request so a refresh is picked up. */
  getToken: () => Promise<string> | string;
  fetch?: typeof globalThis.fetch;
  intervalloMs?: number;
  orologio?: () => number;
}

/** Cadenza di poll predefinita. Hive non pubblica piu' in fretta di cosi', e
 *  il connettore lato server ha una soglia di freschezza di 15 minuti. */
export const INTERVALLO_PREDEFINITO_MS = 30_000;

export class RiconnessioneRichiesta extends ErroreRichiesta {
  constructor() {
    super('riconnetti_account');
    this.name = 'RiconnessioneRichiesta';
  }
}

const ISTANTANEA_VUOTA = (): Istantanea => ({
  ts: 0,
  letture: {},
  attuatori: {},
  stato: null as unknown as StatoImpianto,
  energia: {},
  sorgenti: {},
  allarmi: [],
});

export class CloudClient implements DeviceClient {
  readonly impiantoId: string;
  readonly tecnico: AreaTecnicaClient;

  private readonly opz: Required<Pick<OpzioniCloud, 'intervalloMs'>> & OpzioniCloud;
  private readonly fetch: typeof globalThis.fetch;
  private readonly orologio: () => number;

  private istantanea: Istantanea;
  private ascoltatori = new Set<Ascoltatore>();
  private timer: ReturnType<typeof setInterval> | null = null;
  /** Un poll gia' in volo. Il timer scandisce il tempo, non le richieste: se
   *  la rete rallenta oltre l'intervallo, il tick successivo viene saltato
   *  invece di accodare una seconda richiesta sopra la prima. */
  private inVolo = false;

  constructor(opzioni: OpzioniCloud) {
    // 🚨 I predefiniti si applicano DOPO lo spread, non prima.
    //
    // `{ intervalloMs: 30_000, ...opzioni }` sembra corretto ed e' una trappola:
    // se `opzioni` PORTA la chiave con valore `undefined` — ed e' esattamente
    // cio' che fa `CloudParco.clientPer`, che inoltra sempre
    // `intervalloMs: this.opz.intervalloMs` anche quando nessuno l'ha
    // impostato — lo spread sovrascrive il predefinito con `undefined`.
    // `setInterval(fn, undefined)` non attende: il poll parte alla massima
    // velocita' del browser finche' non esaurisce le socket disponibili
    // (`ERR_INSUFFICIENT_RESOURCES`, migliaia di richieste in pochi secondi).
    // `??` tratta la chiave assente e la chiave `undefined` allo stesso modo,
    // che e' l'unica lettura sensata di "non l'ho scelto".
    const intervalloMs = opzioni.intervalloMs ?? INTERVALLO_PREDEFINITO_MS;
    this.opz = {
      ...opzioni,
      // Un intervallo non positivo e' la stessa bomba scritta a mano.
      intervalloMs: intervalloMs > 0 ? intervalloMs : INTERVALLO_PREDEFINITO_MS,
    };
    this.impiantoId = opzioni.impiantoId;
    this.fetch = opzioni.fetch ?? globalThis.fetch.bind(globalThis);
    this.orologio = opzioni.orologio ?? (() => Date.now());
    this.istantanea = ISTANTANEA_VUOTA();
    this.tecnico = creaTecnicoNonDisponibile();
  }

  // ── reads ───────────────────────────────────────────────────────────

  async getImpianto(): Promise<Impianto> {
    const [registro, reading] = await Promise.all([this.leggiRegistro(), this.leggiReading()]);
    this.aggiorna(reading);
    return costruisciImpianto(registro, reading);
  }

  getIstantanea(): Istantanea {
    return this.istantanea;
  }

  sottoscrivi(ascoltatore: Ascoltatore): () => void {
    this.ascoltatori.add(ascoltatore);
    if (this.timer === null) {
      // Poll immediately, then on the interval. A failed poll degrades the
      // source; it never stops the loop.
      void this.poll();
      this.timer = setInterval(() => void this.poll(), this.opz.intervalloMs);
    }
    return () => {
      this.ascoltatori.delete(ascoltatore);
      if (this.ascoltatori.size === 0 && this.timer !== null) {
        clearInterval(this.timer);
        this.timer = null;
      }
    };
  }

  async getStorico(_richiesta: RichiestaStorico): Promise<PuntoStorico[]> {
    // No history endpoint for a third-party source yet. Empty is honest;
    // fabricating a series would be the opposite of the freshness rule.
    return [];
  }

  async getEventi(): Promise<EventoStato[]> {
    return [];
  }

  async getAllarmi(): Promise<Allarme[]> {
    return [];
  }

  async riconosciAllarme(): Promise<void> {
    /* no alarms from a read-only source */
  }

  async getProgramma(zonaId: string): Promise<ProgrammaOrario> {
    const reading = await this.leggiReading();
    return programmaZona(reading, zonaId);
  }

  // ── commands: refused, at the client and (really) at the server ──────

  async inviaComando(comando: Comando): Promise<RispostaComando> {
    // A third-party source is not commandable (ADR-060 §1, §4). The server
    // refuses too; this is the fast, local half of the same answer.
    return { idComando: comando.idComando, esito: 'fallito', motivo: 'sorgente_non_comandabile' };
  }

  // ── internals ────────────────────────────────────────────────────────

  private async poll(): Promise<void> {
    if (this.inVolo) return;
    this.inVolo = true;
    try {
      const reading = await this.leggiReading();
      this.aggiorna(reading);
    } catch (err) {
      if (err instanceof RiconnessioneRichiesta) {
        this.emettiSorgenteGiu();
        return;
      }
      // A transport failure the API did not already turn into a degraded 200:
      // treat the source as unreachable rather than throwing out of the timer.
      this.emettiSorgenteGiu();
    } finally {
      this.inVolo = false;
    }
  }

  private aggiorna(reading: LetturaImpiantoWire): void {
    // Una sorgente irraggiungibile risponde 200 con `zone: []` — il connettore
    // non puo' elencare zone che non ha appena letto, ed e' giusto cosi'.
    // Ma l'app NON deve dedurne che l'impianto non ha piu' zone: la sua forma
    // non e' cambiata, e' cambiato cio' che ne sappiamo. Sovrascrivere la
    // struttura faceva collassare il pannello a "0 zone", con la scheda
    // ambiente vuota e il sinottico che dichiarava una casa senza stanze —
    // un guasto di rete travestito da impianto smontato.
    //
    // Si conserva quindi l'ultima struttura NOTA e si degrada solo cio' che si
    // misura: nessun valore, `q: 'mancante'`, sorgente giu'. Nessun numero
    // sopravvive a questo passaggio (la freschezza non si finge mai).
    if (!reading.sorgente.raggiungibile && reading.zone.length === 0 && this.istantanea.stato) {
      this.degradaConservandoLaStruttura(reading);
      return;
    }
    this.istantanea = costruisciIstantanea(reading, this.orologio());
    this.emetti({ tipo: 'stato', ts: this.istantanea.ts, stato: this.istantanea.stato });
    this.emetti({ tipo: 'letture', ts: this.istantanea.ts, valori: this.istantanea.letture });
    this.emetti({ tipo: 'sorgente', ts: this.istantanea.ts, sorgente: reading.sorgente });
  }

  /** Mantiene zone, canali e servizi; azzera ogni lettura e marca la sorgente
   *  giu'. Usato quando la sorgente risponde ma non ha nulla da riferire. */
  private degradaConservandoLaStruttura(reading: LetturaImpiantoWire): void {
    const adesso = this.orologio();
    const letture: Istantanea['letture'] = {};
    for (const [canale, precedente] of Object.entries(this.istantanea.letture)) {
      letture[canale] = { ...precedente, v: null, q: 'mancante', ts: precedente.ts };
    }
    this.istantanea = {
      ...this.istantanea,
      ts: reading.sorgente.ultimoAggiornamento ?? this.istantanea.ts,
      letture,
      sorgenti: { ...this.istantanea.sorgenti, hive: reading.sorgente },
    };
    this.emetti({ tipo: 'letture', ts: adesso, valori: letture });
    this.emetti({ tipo: 'sorgente', ts: adesso, sorgente: reading.sorgente });
  }

  private emettiSorgenteGiu(): void {
    const sorgente = { id: 'hive', raggiungibile: false, ultimoAggiornamento: this.istantanea.ts || null };
    this.istantanea = { ...this.istantanea, sorgenti: { ...this.istantanea.sorgenti, hive: sorgente } };
    this.emetti({ tipo: 'sorgente', ts: this.orologio(), sorgente });
  }

  private emetti(evento: Parameters<Ascoltatore>[0]): void {
    for (const a of this.ascoltatori) a(evento);
  }

  private async leggiRegistro(): Promise<ImpiantoRegistro> {
    return this.richiesta<ImpiantoRegistro>(`/v1/plants/${encodeURIComponent(this.impiantoId)}`);
  }

  private async leggiReading(): Promise<LetturaImpiantoWire> {
    return this.richiesta<LetturaImpiantoWire>(`/v1/plants/${encodeURIComponent(this.impiantoId)}/readings`);
  }

  private async richiesta<T>(percorso: string): Promise<T> {
    const token = await this.opz.getToken();
    const resp = await this.fetch(`${this.opz.baseUrl}${percorso}`, {
      headers: { authorization: `Bearer ${token}`, accept: 'application/json' },
    });
    if (resp.status === 409) throw new RiconnessioneRichiesta();
    if (!resp.ok) throw new ErroreRichiesta(`api_${resp.status}`);
    return (await resp.json()) as T;
  }
}

function creaTecnicoNonDisponibile(): AreaTecnicaClient {
  const no = (): never => {
    throw new ErroreRichiesta('area_tecnica_non_disponibile_terze_parti');
  };
  return {
    accedi: no,
    getCanali: no,
    patchCanale: no,
    patchZonaCarico: no,
    getParametri: no,
    patchParametri: no,
    getGeneratori: no,
    putGeneratori: no,
    forzatura: no,
    annullaForzatura: no,
    getRegistro: no,
    getOta: no,
    avviaOta: no,
    rollbackOta: no,
  } as unknown as AreaTecnicaClient;
}
